import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartPanelItemTileLayout from './CartPanelItemTileLayout'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelActiveHotelTile({
  item,
  onToggleInclude,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const changeRoomLabel = intl.formatMessage({
    id: 'BrfTYdJonj0A3hETLQR8',
    defaultMessage: 'Change room',
    description: 'Link to change the selected room for a hotel in the cart panel',
  })

  const deleteLabel = intl.formatMessage({
    id: 'jDEZjhV3eo-cnxIBHOtR',
    defaultMessage: 'Delete',
    description: 'Button to permanently delete an item from the trip planner cart panel',
  })

  const defaultRoomLabel = intl.formatMessage({
    id: 'm1vHQfyx83yPvfgh-Ma4',
    defaultMessage: 'Default room picked for you',
    description: 'Label indicating the room was auto-selected by the system in the cart panel',
  })

  const soldOutLabel = intl.formatMessage({
    id: '33gzZELMLS52IVDsupWp',
    defaultMessage: 'Sold out for your dates',
    description: 'Warning banner shown when a hotel in the cart panel is not available for the selected dates',
  })

  const isSoldOut = item.pricing.isAvailable === false

  const datesLabel = item.dates && item.duration
    ? intl.formatMessage(
        {
          id: 'VElcmYw8-6lH-Mgm2GV6',
          defaultMessage: '{dateRange} · {nights, plural, one {# night} other {# nights}}',
          description: 'Date range with duration in nights for a hotel stay tile in the trip planner cart panel',
        },
        { dateRange: item.dates, nights: item.duration },
      )
    : item.dates

  const content = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading5">{item.name}</Heading>
      {datesLabel && (
        <BodyText variant="medium" colour="neutral-three">
          {datesLabel}
        </BodyText>
      )}
      {item.roomName && <BodyText variant="medium">{item.roomName}</BodyText>}
      {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
      {item.packageAutoSelected && !isSoldOut && (
        <HighlightLabel variant="primaryLight" startIcon={<SolidSparklesIcon />}>
          {defaultRoomLabel}
        </HighlightLabel>
      )}
      {isSoldOut && (
        <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
          {soldOutLabel}
        </MessageBanner>
      )}
    </Group>
  )

  const actions = (
    <Group direction="horizontal" gap={8}>
      <TextButton kind="tertiary" size="small" onClick={handleChangeRoom} disabled={isStreaming}>
        {changeRoomLabel}
      </TextButton>
      <TextButton kind="tertiary" size="small" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
        {deleteLabel}
      </TextButton>
    </Group>
  )

  const price = item.pricing.price && !isSoldOut ? (
    <CartReviewPriceStack
      price={item.pricing.price}
      memberPrice={item.pricing.memberPrice}
      value={item.pricing.value ?? 0}
    />
  ) : undefined

  return (
    <CartPanelItemTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      content={content}
      attention={undefined}
      actions={actions}
      price={price}
    />
  )
}

export default CartPanelActiveHotelTile
